import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import api from '../api/api';
import { useAuth } from './AuthContext';

const OnboardingContext = createContext(null);

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) throw new Error('useOnboarding must be used within an OnboardingProvider');
  return context;
};

export const OnboardingProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [onboardingCompleted, setOnboardingCompleted] = useState(true);
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [isFirstLogin, setIsFirstLogin] = useState(false);
  const [tourRunning, setTourRunning] = useState(false);
  const [demoMode, setDemoMode] = useState(false);
  const [hasDemoData, setHasDemoData] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }
    const fetchStatus = async () => {
      try {
        const res = await api.get('/onboarding/status');
        setOnboardingCompleted(res.data.onboardingCompleted);
        setOnboardingStep(res.data.onboardingStep);
        setIsFirstLogin(res.data.isFirstLogin);

        if (!res.data.onboardingCompleted && res.data.isFirstLogin) {
          setTourRunning(true);
        }

        const demoRes = await api.get('/onboarding/demo/status');
        setHasDemoData(demoRes.data.hasDemoData);
      } catch (err) {
        console.error('Erreur chargement statut onboarding:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStatus();
  }, [currentUser]);

  const updateStep = useCallback(async (step) => {
    setOnboardingStep(step);
    try {
      await api.put('/onboarding/status', { onboardingStep: step });
    } catch (err) {
      console.error('Erreur mise à jour étape onboarding:', err);
    }
  }, []);

  const completeOnboarding = useCallback(async () => {
    setOnboardingCompleted(true);
    setTourRunning(false);
    try {
      await api.put('/onboarding/status', { onboardingCompleted: true, onboardingStep: -1 });
    } catch (err) {
      console.error('Erreur complétion onboarding:', err);
    }
  }, []);

  const skipOnboarding = useCallback(() => {
    setOnboardingCompleted(true);
    setTourRunning(false);
    api.put('/onboarding/status', { onboardingCompleted: true, onboardingStep: -1 }).catch(() => {});
  }, []);

  const startTour = useCallback(() => {
    setTourRunning(true);
    setOnboardingCompleted(false);
  }, []);

  const seedDemoData = useCallback(async () => {
    try {
      const res = await api.post('/onboarding/demo/seed');
      setHasDemoData(true);
      setDemoMode(true);
      return res.data;
    } catch (err) {
      throw err;
    }
  }, []);

  const resetDemoData = useCallback(async () => {
    try {
      await api.post('/onboarding/demo/reset');
      setHasDemoData(false);
      setDemoMode(false);
    } catch (err) {
      throw err;
    }
  }, []);

  const value = {
    onboardingCompleted,
    onboardingStep,
    isFirstLogin,
    tourRunning,
    demoMode,
    hasDemoData,
    loading,
    updateStep,
    completeOnboarding,
    skipOnboarding,
    startTour,
    seedDemoData,
    resetDemoData,
    setDemoMode,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

OnboardingProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
